// 2026-06-25: Phase 9 — Rework Orders page
// Spawned from NC dispositioned as REWORK. Operator claims with qty recovered/scrap.
import { cleanCustomerId } from '../../Services/app/operatorservice';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { listReworkOrders, claimReworkOrder } from '../../Services/app/workorderservice';
import Swal from 'sweetalert2';
import '../NcLog/NcLog.css';

const STATUS_COLOR = {
  CREATED: '#9ca3af', READY: '#2563eb', IN_PROGRESS: '#f59e0b',
  CLAIMED: '#16a34a', DONE: '#16a34a', CANCELLED: '#6b7280',
};

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const ist = new Date(d.getTime() + 5.5 * 3600_000);
  return ist.toISOString().slice(0, 16).replace('T', ' ');
}

export default function ReworkOrders() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('OPEN');
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try { setRows(await listReworkOrders(customerId) || []); } catch (e) { console.error(e); }
    setLoading(false);
  }, [customerId]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    if (filter === 'ALL') return rows;
    if (filter === 'OPEN') return rows.filter((r) => ['CREATED', 'READY', 'IN_PROGRESS'].includes(r.status));
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const counts = useMemo(() => ({
    ALL: rows.length,
    OPEN: rows.filter((r) => ['CREATED', 'READY', 'IN_PROGRESS'].includes(r.status)).length,
    DONE: rows.filter((r) => r.status === 'DONE').length,
  }), [rows]);

  return (
    <div className="nc-page pages" style={{ paddingTop: 32 }}>
      <div className="nc-header">
        <div>
          <h5 style={{ margin: 0 }}>Rework Orders</h5>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Spawned from NC dispositions · Operator claims rework outcome
          </div>
        </div>
        <button className="nc-btn-secondary" onClick={fetchAll} disabled={loading}>Refresh</button>
      </div>

      <div className="nc-tabs">
        {['OPEN', 'DONE', 'ALL'].map((t) => (
          <button key={t} className={`nc-tab ${filter === t ? 'nc-tab-active' : ''}`} onClick={() => setFilter(t)}>
            {t} <span className="nc-tab-count">{counts[t] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? <div style={{ padding: 30, color: '#9ca3af' }}>Loading…</div> :
        filtered.length === 0 ? <div style={{ padding: 30, color: '#9ca3af' }}>No rework orders in this view.</div> : (
        <div className="nc-grid">
          {filtered.map((r) => (
            <div key={r.id} className="nc-card" style={{ borderLeftColor: STATUS_COLOR[r.status] }}>
              <div className="nc-card-head">
                <div>
                  <div className="nc-num">{r.rework_wo_number}</div>
                  <div className="nc-meta">Spawned {fmtTime(r.created_ts)}</div>
                </div>
                <span className="nc-pill" style={{ background: STATUS_COLOR[r.status] }}>{r.status}</span>
              </div>
              <div className="nc-row"><span>Parent WO</span><strong>{r.parent_wo_number} · {r.parent_op_seq}</strong></div>
              <div className="nc-row"><span>NC ref</span><strong>{r.nc_number}</strong></div>
              <div className="nc-row"><span>Rework op</span><strong>{r.rework_op_seq} — {r.rework_op_name}</strong></div>
              <div className="nc-row"><span>Target WC</span><strong>{r.target_work_center_code || '—'}</strong></div>
              <div className="nc-row"><span>Qty to rework</span><strong>{r.qty_to_rework}</strong></div>
              {r.estimated_cost > 0 && (
                <div className="nc-row"><span>Est. cost</span><strong>₹{Number(r.estimated_cost).toLocaleString('en-IN')}</strong></div>
              )}
              {r.status === 'DONE' && (
                <div style={{ marginTop: 6, padding: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
                  <div className="nc-row"><span>Recovered</span><strong style={{ color: '#16a34a' }}>{r.actual_qty_recovered} pcs</strong></div>
                  <div className="nc-row"><span>Lost (scrap)</span><strong style={{ color: '#dc2626' }}>{r.actual_qty_scrap} pcs</strong></div>
                  <div className="nc-row"><span>Actual cost</span><strong>₹{Number(r.actual_cost).toLocaleString('en-IN')}</strong></div>
                  <div className="nc-row"><span>Claimed by</span><strong>{r.claimed_by_operator_name || '—'}</strong></div>
                </div>
              )}
              {['CREATED', 'READY', 'IN_PROGRESS'].includes(r.status) && (
                <div className="nc-actions">
                  <button className="nc-btn-primary" onClick={() => setClaiming(r)}>Claim rework</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {claiming && (
        <RwClaimDialog
          rw={claiming}
          customerId={customerId}
          onClose={() => setClaiming(null)}
          onClaimed={() => { setClaiming(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

function RwClaimDialog({ rw, customerId, onClose, onClaimed }) {
  const [form, setForm] = useState({
    operator_name: '',
    qty_recovered: rw.qty_to_rework,
    qty_scrap: 0,
    actual_cost: rw.estimated_cost,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.operator_name) { Swal.fire('Missing', 'Operator name required', 'warning'); return; }
    if (Number(form.qty_recovered) + Number(form.qty_scrap) > rw.qty_to_rework) {
      const ok = await Swal.fire({
        icon: 'warning',
        title: 'Total qty exceeds rework qty',
        text: `Recovered + Scrap (${Number(form.qty_recovered) + Number(form.qty_scrap)}) > To-rework (${rw.qty_to_rework}). Save anyway?`,
        showCancelButton: true,
      });
      if (!ok.isConfirmed) return;
    }
    setSaving(true);
    try {
      await claimReworkOrder(rw.id, { ...form, customer_id: customerId });
      Swal.fire({ icon: 'success', title: 'Rework claimed', timer: 1500, showConfirmButton: false });
      onClaimed();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
    setSaving(false);
  };

  return (
    <div className="nc-modal-backdrop" onClick={onClose}>
      <div className="nc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nc-modal-head">
          <h6>Claim Rework — {rw.rework_wo_number}</h6>
          <button onClick={onClose}>×</button>
        </div>
        <div className="nc-modal-body">
          <div className="nc-dispose-summary">
            <div><strong>{rw.parent_wo_number}</strong> · {rw.rework_op_name}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Qty to rework: {rw.qty_to_rework} · {rw.target_work_center_code}
            </div>
          </div>
          <label>Operator name *</label>
          <input type="text" value={form.operator_name}
                 onChange={(e) => setForm({ ...form, operator_name: e.target.value })} />
          <div className="nc-grid-2">
            <div>
              <label>Recovered (good pcs)</label>
              <input type="number" min="0" value={form.qty_recovered}
                     onChange={(e) => setForm({ ...form, qty_recovered: e.target.value })} />
            </div>
            <div>
              <label>Lost to scrap</label>
              <input type="number" min="0" value={form.qty_scrap}
                     onChange={(e) => setForm({ ...form, qty_scrap: e.target.value })} />
            </div>
          </div>
          <label>Actual cost incurred (₹)</label>
          <input type="number" min="0" value={form.actual_cost}
                 onChange={(e) => setForm({ ...form, actual_cost: e.target.value })} />
          <label>Notes</label>
          <textarea rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="What was done?" />
        </div>
        <div className="nc-modal-foot">
          <button className="nc-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="nc-btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Claim & close'}
          </button>
        </div>
      </div>
    </div>
  );
}
